import type { PropsWithChildren } from "react";
import { useCallback } from "react";

import { SessionHandler } from "@sendbird/chat";
import { SendBirdProvider } from "@sendbird/uikit-react";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import useAuth from "@/hooks/useAuth";
import useAppMonitor from "@/services/AppMonitor";
import {
  useGetUserChatTokenQuery,
  useLazyGetUserChatTokenQuery,
} from "@/store/api/chat";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

const SendbirdWebProviderWrapper = ({ children }: PropsWithChildren) => {
  const { externalId: appUserId } = useAppUserSelector();
  const { isAuthenticated } = useAuth();
  const { captureException } = useAppMonitor();
  const { data: sendbirdChatTokenData } = useGetUserChatTokenQuery(undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- skip naming is defined by RTKQuery
    skip: !isAuthenticated,
  });
  const [refetchSendbirdToken] = useLazyGetUserChatTokenQuery();

  // web SDK fires onSessionTokenRequired when its session_key expires, to refresh the token
  const configureSession = useCallback(
    () =>
      new SessionHandler({
        onSessionClosed: () => {
          captureException(new Error("Sendbird session closed"));
        },
        onSessionError: captureException,
        // eslint-disable-next-line max-params -- SDK contract: (resolve, reject) => void
        onSessionTokenRequired: (resolve, reject) => {
          void refetchSendbirdToken()
            .unwrap()
            .then((result) => resolve(result.token))
            .catch((error) => {
              captureException(error);
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- SDK contract: reject(Error)
              reject(error as Error);
            });
        },
      }),
    [captureException, refetchSendbirdToken],
  );

  // Sendbird auto-connects once appId/userId are both non-empty; however, if accessToken is not yet ready, the auto-connect will fail.
  // configureSession also only re-connects if an existing valid session has its token expired, not if init fails.
  // as a result, this would cause Sendbird auto-connect to fail, and stay failed indefinitely.

  // so instead, userId here is only surfaced as a non-undefined value once the accessToken is also ready.
  // additionally, userId=undefined will not trigger an initial sendbird connect API call, whereas the default "" for appUserId will (and will fail).
  const hasAccessToken = isNotNilNotEmpty(sendbirdChatTokenData?.token);
  const hasUserId = isNotNilNotEmpty(appUserId);
  const [accessToken, userId] =
    hasAccessToken && hasUserId
      ? [sendbirdChatTokenData.token, appUserId]
      : [undefined, undefined];

  return (
    <SendBirdProvider
      accessToken={accessToken}
      appId={ProcessEnvironment.SendbirdAppId}
      // activate mobile mode of sendbird web SDK
      breakpoint
      configureSession={configureSession}
      // sendbird web uses userId to identify if a message "isByMe", as well as to auto-connect; which is by purely imperative connect/disconnect would be insufficient
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- sendbird's userId prop is typed as string but accepts undefined while unauthenticated
      userId={userId as unknown as string}
    >
      {children}
    </SendBirdProvider>
  );
};

export default SendbirdWebProviderWrapper;
